import { apiFetch, toArray } from '@/services/core/apiFetch'
import { getContext } from '@/services/networks/networksApi'
import { AuthService } from '@/services/core/auth'
import { fillPlaceholders, throwApiError } from '@/services/core/apiError'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface Asset {
  id: string
  name: string
  objectState: string
  fileName: string
  mimeType: string
  /** Weight of the file in bytes — the API calls this fileSizeBytes. */
  fileSize: number
  width: number
  height: number
  cdnId: string
  previewCdnId: string
  /** Set when the asset was uploaded as a zip (an HTML creative); the download source. */
  zipCdnId: string
  zipName: string
  /** The HTML file a zipped HTML creative opens with — the one a layout points at. */
  primaryHtmlAsset: boolean
  updateTime: string
  createTime: string
}

/** The API returns mimeType as an enum string (e.g. "VIDEO_MP4") or MIME type (e.g. "video/mp4") */
export function isVideoAsset(mimeType: string): boolean {
  return mimeType.startsWith('VIDEO_') || mimeType.startsWith('video/')
}

const HTML_MIME_TYPES = ['TEXT_HTML', 'TEXT_XHTML', 'text/html', 'application/xhtml+xml']

export function isHtmlAsset(mimeType: string): boolean {
  return HTML_MIME_TYPES.includes(mimeType)
}

function mapAsset(r: Record<string, unknown>): Asset {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    fileName: (r.fileName as string) ?? '',
    mimeType: (r.mimeType as string) ?? '',
    fileSize: ((r.fileSizeBytes ?? r.fileSize) as number) ?? 0,
    width: (r.width as number) ?? 0,
    height: (r.height as number) ?? 0,
    cdnId: (r.cdnId as string) ?? '',
    previewCdnId: (r.previewCdnId as string) ?? '',
    zipCdnId: (r.zipCdnId as string) ?? '',
    zipName: (r.zipName as string) ?? '',
    primaryHtmlAsset: (r.primaryHtmlAsset as boolean) ?? false,
    updateTime: (r.updateTime as string) ?? '',
    createTime: (r.createTime as string) ?? '',
  }
}

/**
 * Posts one file as multipart form data through XHR, which unlike fetch
 * reports upload progress. Resolves with the parsed JSON response.
 */
function uploadWithProgress(
  url: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${url}?context=${getContext()}`)

    // Forward auth token if present
    const token = AuthService.getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      let body: unknown
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        body = undefined
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (body === undefined) reject(new Error('Invalid JSON response'))
        else resolve(body)
        return
      }
      // Validation failures carry the server's reason, e.g. an unsupported file type.
      const err = body as
        | {
            text?: string
            errors?: { error?: { text?: string; parameters?: Record<string, string> } }[]
          }
        | undefined
      const reason =
        err?.errors
          ?.map((e) => e.error?.text && fillPlaceholders(e.error.text, e.error.parameters))
          .filter(Boolean)
          .join('\n') || err?.text
      reject(new Error(reason || `Failed to upload asset (${xhr.status})`))
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(formData)
  })
}

export const AssetsApi = {
  getAll: async (): Promise<Asset[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/assets?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch assets (${res.status})`)
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? data
    return results.map(mapAsset)
  },

  getById: async (id: string): Promise<Asset> => {
    const res = await apiFetch(`${API_BASE}/assets/${id}?context=${getContext()}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch asset (${res.status})`)
    return mapAsset(await res.json())
  },

  upload: async (file: File): Promise<Asset> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiFetch(`${API_BASE}/assets?context=${getContext()}`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) throw new Error(`Failed to upload asset (${res.status})`)
    return mapAsset(await res.json())
  },

  /**
   * Uploads a zipped HTML creative. The server unpacks it into one asset per
   * file — ids `<assetId>_1`, `<assetId>_2`… — and flags the HTML file it
   * found as the primary one.
   */
  uploadZipForCreative: async (creativeId: string, assetId: string, zip: File): Promise<Asset[]> => {
    const formData = new FormData()
    formData.append('file', zip)
    const res = await apiFetch(`${API_BASE}/assets/${creativeId}/${assetId}?context=${getContext()}`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) await throwApiError(res, `Failed to upload zip (${res.status})`)
    return toArray(await res.json()).map(mapAsset)
  },

  uploadForCreative: async (creativeId: string, file: File): Promise<Asset> => {
    return AssetsApi.uploadForCreativeWithProgress(creativeId, file)
  },

  uploadForCreativeWithProgress: async (
    creativeId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<Asset> => {
    const assetId = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    const data = await uploadWithProgress(`${API_BASE}/assets/${creativeId}/${assetId}`, file, onProgress)
    return mapAsset(data as Record<string, unknown>)
  },

  /**
   * Uploads a file that no creative owns yet. The server hands the asset to
   * the first creative saved with it in its layout parameters, so a bulk
   * upload can send its files before any creative exists.
   */
  uploadUnattached: async (file: File, onProgress?: (pct: number) => void): Promise<Asset> => {
    const assetId = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    const data = await uploadWithProgress(`${API_BASE}/asset/${assetId}`, file, onProgress)
    // A zip answers with every file it unpacked; anything else with the one asset.
    const assets = toArray<Record<string, unknown>>(data)
    const primary = assets.find((a) => a.primaryHtmlAsset) ?? assets[0]
    if (!primary) throw new Error('Upload returned no asset')
    return mapAsset(primary)
  },

  /**
   * Uploads a file straight to the CDN, owned by nothing — for images that
   * markup elsewhere links to by URL, such as a release note's. The returned
   * `cdnId` is that URL.
   */
  uploadToCdn: async (file: File): Promise<Asset> => {
    const data = await uploadWithProgress(`${API_BASE}/cdnassets/${crypto.randomUUID()}`, file)
    const asset = toArray<Record<string, unknown>>(data)[0]
    if (!asset) throw new Error('Upload returned no asset')
    return mapAsset(asset)
  },

  getByCreativeAndAsset: async (creativeId: string, assetId: string): Promise<Asset> => {
    const res = await apiFetch(`${API_BASE}/assets/${creativeId}/${assetId}?context=${getContext()}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch asset (${res.status})`)
    return mapAsset(await res.json())
  },

  getByCreative: async (creativeId: string): Promise<Asset[]> => {
    const params = new URLSearchParams({ format: 'json', concise: 'true', pageSize: '1000', context: getContext() })
    const res = await apiFetch(`${API_BASE}/assets/${creativeId}?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch assets (${res.status})`)
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? (Array.isArray(data) ? data : [data])
    return results.map(mapAsset)
  },
}
